import { Observable, throwError as observableThrowError } from 'rxjs';
import { isUndefined } from 'lodash';
import {
  ACCEPT_VERSION_HEADER,
  AuthHttpService,
} from '../auth-data/auth-http.service';
import { EntityDataService } from '../entity-data/entity-data.service';
import { ErrorService } from '../error-reporting/error.service';
import { IResponseInfo } from '../http/http.data';
import { RendererService } from '../renderer/renderer.service';
import { SettingsDataStore } from '../settings-data/settings-data.store';
import { LoggerService } from '../shared/services/logger/logger.service';
import {
  DOC_ACCEPT_VERSION,
  DOC_FILTER_ACCEPT_VERSION,
  DOC_SEARCH_ACCEPT_VERSION,
  DocumentFiltersEnum,
} from './document-data.data';
import { DocumentFilters } from './filters/document-filters.model';
import { catchError, map } from 'rxjs/operators';
import {
  IAddPaymentPayload,
  PaymentErrorsEnum,
} from '../payments-data/payments-data.data';
import { IDocumentDataBuilder } from './document-data-builder.interface';

// should only be used as a superclass; this class is not injectable
export class DocumentDataService extends EntityDataService {
  protected inputHeaders: any = {
    [ACCEPT_VERSION_HEADER]: DOC_ACCEPT_VERSION,
  };
  protected searchHeaders: any = {
    [ACCEPT_VERSION_HEADER]: DOC_SEARCH_ACCEPT_VERSION,
  };
  protected filtersHeaders: any = {
    [ACCEPT_VERSION_HEADER]: DOC_FILTER_ACCEPT_VERSION,
  };

  constructor(
    public documentDataUrl: string,
    protected authHttpService: AuthHttpService,
    protected documentDataBuilder: IDocumentDataBuilder,
    private _loggerService: LoggerService,
    private _rendererService: RendererService,
    errorService: ErrorService,
    settingsDataStore: SettingsDataStore
  ) {
    super(
      authHttpService,
      documentDataUrl,
      _loggerService,
      errorService,
      documentDataBuilder,
      settingsDataStore
    );
  }

  getFilters(listFilter: DocumentFiltersEnum): Observable<DocumentFilters> {
    const params =
      !isUndefined(listFilter) && listFilter !== DocumentFiltersEnum.all
        ? { list_category: listFilter }
        : {};
    const url = `${this.entitiesUrl}/filters`;

    return this.authHttpService.get(url, params, this.filtersHeaders).pipe(
      map(({ body, ..._ }: IResponseInfo) => new DocumentFilters(body)),
      catchError((error) => observableThrowError(error))
    );
  }

  addManualPayment(
    id: string,
    payload: IAddPaymentPayload
  ): Observable<IResponseInfo> {
    return this.authHttpService.post(
      `${this.documentDataUrl}/${id}/payments/manual_transactions`,
      payload,
      this.inputHeaders
    );
  }

  deleteManualPayment(
    documentId: string,
    transactionId: string
  ): Observable<IResponseInfo> {
    return this.authHttpService.delete(
      `${this.documentDataUrl}/${documentId}/payments/manual_transactions/${transactionId}`,
      {},
      this.inputHeaders
    );
  }

  refundTransaction(
    documentId: string,
    transactionId: string
  ): Observable<IResponseInfo> {
    return this.authHttpService.post(
      `${this.documentDataUrl}/${documentId}/payments/e_transactions/${transactionId}/refund`,
      {},
      this.inputHeaders,
      true,
      'json',
      [PaymentErrorsEnum.transaction_refunded]
    );
  }

  unmatchTransaction(
    documentId: string,
    transactionId: string
  ): Observable<IResponseInfo> {
    return this.authHttpService.post(
      `${this.documentDataUrl}/${documentId}/payments/${transactionId}/unmatch`,
      {},
      this.inputHeaders,
      true,
      'json',
      [PaymentErrorsEnum.transaction_unmatched]
    );
  }

  markAsPrinted(documentId: string): Observable<any> {
    return this.authHttpService
      .post(
        `${this.documentDataUrl}/${documentId}/events/printed`,
        {},
        this.inputHeaders
      )
      .pipe(
        map(({ body, ..._ }: IResponseInfo) => body[this.topLevelKeys.putKey])
      );
  }

  sendDocument(documentId: string, payload: any): Observable<any> {
    return this._rendererService
      .sendDocument(this.documentDataUrl, documentId, payload)
      .pipe(
        map(({ body, ..._ }: IResponseInfo) => body[this.topLevelKeys.putKey])
      );
  }
}
